import * as yup from 'yup'
import { HealthUnitType } from './unitType'

/**
 * Faixa aceita pelo backend em `CreateHealthUnitDto`/`PatchHealthUnitDto`
 * (bng-health/minhaescala_backend#2480). As mensagens espelham as do DTO para
 * que o usuário veja o mesmo texto validando no front ou vindo do 400.
 */
export const REACH_RADIUS_MIN = 50
export const REACH_RADIUS_MAX = 500
export const REACH_RADIUS_HINT = `Entre ${REACH_RADIUS_MIN} e ${REACH_RADIUS_MAX} metros`

/**
 * Sugestão inicial do campo: cobre a área de um prédio hospitalar com folga e
 * poupa o usuário de adivinhar um número dentro da faixa. Continua editável.
 */
export const REACH_RADIUS_DEFAULT = 150

// Mensagens só saem daqui pelo schema — quem valida o campo usa o schema, não a string.
const REACH_RADIUS_MIN_MESSAGE = `O raio de alcance mínimo é de ${REACH_RADIUS_MIN} metros.`
const REACH_RADIUS_MAX_MESSAGE = `O raio de alcance máximo é de ${REACH_RADIUS_MAX} metros.`
const REACH_RADIUS_TYPE_MESSAGE = 'O raio de alcance deve ser um número'

/** Vazio, nulo e não numérico ficam fora da faixa — sem raio não há confirmação. */
export const isReachRadiusInRange = (value: unknown): boolean => {
  const radius = Number(value)

  return (
    value !== null &&
    value !== undefined &&
    value !== '' &&
    Number.isFinite(radius) &&
    radius >= REACH_RADIUS_MIN &&
    radius <= REACH_RADIUS_MAX
  )
}

const UNIT_TYPE_MESSAGE = 'Tipo de unidade é obrigatório'

export const HealthUnitSchema = yup.object({
  name: yup.string().required('Nome é obrigatório'),
  /**
   * Espelha `HealthUnitType` do backend. Obrigatório: o tipo define o preset de
   * raio sugerido e a leitura da fila de revisão do pino.
   */
  type: yup
    .mixed<HealthUnitType>()
    .oneOf(Object.values(HealthUnitType), UNIT_TYPE_MESSAGE)
    .required(UNIT_TYPE_MESSAGE),
  email: yup.string().email('Digite um email válido').optional(),
  avatarUrl: yup.string().optional(),
  // O campo do formulário guarda o CNPJ mascarado — a regra vale sobre os dígitos.
  cnpj: yup
    .string()
    .test(
      'cnpj-digits',
      'CNPJ deve conter 14 dígitos',
      (value) => !value || value.replace(/\D/g, '').length === 14
    )
    .required('CNPJ é obrigatório'),
  profile: yup.string().optional(),
  quantityColaborators: yup
    .number()
    .integer()
    .optional()
    .typeError('Quantidade deve ser um número'),
  generalManager: yup.string().optional(),
  technicalManager: yup.string().optional(),
  // O campo do formulário guarda o telefone mascarado — a regra vale sobre os dígitos.
  mainPhone: yup
    .string()
    .optional()
    .test(
      'phone-digits',
      'Telefone deve ter 10 ou 11 dígitos',
      (value) => !value || [10, 11].includes(value.replace(/\D/g, '').length)
    ),

  // O campo do formulário guarda o CEP mascarado — a regra vale sobre os dígitos.
  postalCode: yup
    .string()
    .test(
      'postal-code-digits',
      'CEP deve conter 8 dígitos',
      (value) => !value || value.replace(/\D/g, '').length === 8
    )
    .required('CEP é obrigatório'),
  state: yup.string().required('Estado é obrigatório'),
  city: yup.string().required('Campo obrigatório'),
  street: yup.string().required('Campo obrigatório'),
  number: yup.string().required('Número é obrigatório'),
  neighborhood: yup.string().required('Bairro é obrigatório'),
  complement: yup.string().optional(),
  latitude: yup.string().optional(),
  longitude: yup.string().optional(),
  linkWaze: yup.string().optional(),
  reachRadius: yup
    .number()
    // Campo vazio é "sem raio", não um número inválido (o backend aceita ausente).
    .transform((value, original) => (original === '' || original === null ? undefined : value))
    .typeError(REACH_RADIUS_TYPE_MESSAGE)
    .min(REACH_RADIUS_MIN, REACH_RADIUS_MIN_MESSAGE)
    .max(REACH_RADIUS_MAX, REACH_RADIUS_MAX_MESSAGE)
    .optional(),
  operationalGroup: yup.string().required('Grupo Operacional é obrigatório')
})

/**
 * `type` fica opcional no estado do formulário (vazio enquanto o usuário não
 * escolhe) — a obrigatoriedade é do schema, cobrada no submit.
 */
export type HealthUnit = Omit<yup.InferType<typeof HealthUnitSchema>, 'type'> & {
  type?: HealthUnitType
}

export type ErrorUnit = { [K in keyof HealthUnit]: string }

/**
 * Factories de estado do formulário — um objeto NOVO por chamada.
 * (Substituem o antigo singleton mutável `states`, que vazava estado entre
 * aberturas do modal.)
 */
export const createEmptyHealthUnitValues = (): HealthUnit => ({
  name: '',
  type: undefined,
  email: undefined,
  avatarUrl: undefined,
  cnpj: '',
  generalManager: undefined,
  mainPhone: undefined,
  profile: undefined,
  quantityColaborators: 0,
  technicalManager: undefined,
  city: '',
  complement: undefined,
  latitude: undefined,
  longitude: undefined,
  neighborhood: '',
  number: '',
  postalCode: '',
  state: '',
  street: '',
  linkWaze: '',
  reachRadius: undefined,
  operationalGroup: ''
})

export const createEmptyHealthUnitErrors = (): ErrorUnit => ({
  email: '',
  name: '',
  type: '',
  avatarUrl: '',
  cnpj: '',
  generalManager: '',
  mainPhone: '',
  profile: '',
  quantityColaborators: '',
  technicalManager: '',
  city: '',
  complement: '',
  latitude: '',
  longitude: '',
  neighborhood: '',
  number: '',
  postalCode: '',
  state: '',
  street: '',
  linkWaze: '',
  reachRadius: '',
  operationalGroup: ''
})
